import { useMemo } from "react";
import type { Auction } from "@/services/types";
import { computeCommission } from "@/utils/auction";
import { Card } from "@/components/base/Card";
import { Badge } from "@/components/base/Badge";
import { Alert } from "@/components/base/Alert";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { formatDateTime } from "@/utils/format";
import { HeaderBand } from "./HeaderBand";

interface ClosedAuctionViewProps {
  auction: Auction;
}

/**
 * The read-only audit record of a closed auction. Shows the winner, the winning
 * bid, the full calculation breakdown, the complete bid history including
 * withdrawn bids, and who closed it and when. This is what staff show a member
 * who questions an outcome weeks later.
 */
export function ClosedAuctionView({ auction }: ClosedAuctionViewProps) {
  const result = auction.result;
  const nameMap = useMemo(() => {
    const map = new Map<string, { name: string; ticket: string }>();
    [...auction.eligible, ...auction.ineligible].forEach((m) => map.set(m.memberId, { name: m.fullName, ticket: m.ticketNumber }));
    return map;
  }, [auction]);

  const commission = useMemo(() => {
    if (!result) return 0;
    if (result.commission != null) return result.commission;
    const perMemberDiv = result.perMemberDividend ?? 0;
    return computeCommission(auction.commissionType, auction.commissionValue, auction.pool, result.discount);
  }, [auction, result]);

  if (!result) {
    return <Alert variant="error">This cycle has no recorded result.</Alert>;
  }

  const winner = nameMap.get(result.winnerMemberId);
  const history = [...auction.bids].sort((a, b) => a.amount - b.amount);

  return (
    <div>
      <HeaderBand
        auction={auction}
        pool={auction.pool}
        currentLowest={result.winningBid}
        currentInstallment={result.netInstallment}
      />

      <div className="mb-4 flex items-center gap-2">
        <Badge variant="completed" dot>Closed</Badge>
        <span className="text-xs text-foreground-500">
          Closed by <span className="font-medium text-foreground-700">{result.closedBy}</span> on{" "}
          <span className="tnum">{formatDateTime(result.closedAt)}</span>
        </span>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Card title="Result" subtitle={`Cycle ${auction.cycle} · ${auction.periodLabel}`}>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="rounded-md bg-accent-100 px-4 py-3">
                <p className="text-xs font-medium uppercase tracking-wide text-accent-800">Winning member</p>
                <p className="text-lg font-semibold text-accent-900">{winner?.name ?? "—"}</p>
                <p className="tnum text-sm text-accent-800">
                  {winner?.ticket ?? ""} · received <CurrencyDisplay amount={result.winningBid} hideSymbol />
                </p>
              </div>
              <div className="rounded-md bg-background-100 px-4 py-3">
                <p className="text-xs font-medium uppercase tracking-wide text-foreground-500">Payout after dues</p>
                <p className="text-lg font-semibold text-foreground-950"><CurrencyDisplay amount={result.winnerPayout} hideSymbol /></p>
                <p className="tnum text-sm text-foreground-600">
                  pending dues <CurrencyDisplay amount={result.winnerPendingDues} hideSymbol />
                </p>
              </div>
            </div>

            <dl className={`mt-4 grid grid-cols-2 gap-x-6 gap-y-2 text-sm ${commission > 0 ? "sm:grid-cols-6" : "sm:grid-cols-4"}`}>
              <div>
                <dt className="text-xs text-foreground-500">Winning bid</dt>
                <dd className="tnum mt-0.5 font-semibold text-foreground-900"><CurrencyDisplay amount={result.winningBid} hideSymbol /></dd>
              </div>
              <div>
                <dt className="text-xs text-foreground-500">Discount</dt>
                <dd className="tnum mt-0.5 font-semibold text-danger-600"><CurrencyDisplay amount={result.discount} hideSymbol /></dd>
              </div>
              <div>
                <dt className="text-xs text-foreground-500">Dividend / member (−)</dt>
                <dd className="tnum mt-0.5 font-semibold text-success-700">− <CurrencyDisplay amount={result.perMemberDividend} hideSymbol /></dd>
                {result.dividendRemainder > 0 ? (
                  <dd className="tnum text-xs text-warning-600">+ {result.dividendRemainder} remainder</dd>
                ) : null}
              </div>
              {commission > 0 ? (
                <>
                  <div>
                    <dt className="text-xs text-foreground-500">Total commission</dt>
                    <dd className="tnum mt-0.5 font-semibold text-primary-700"><CurrencyDisplay amount={commission} hideSymbol /></dd>
                  </div>
                  <div>
                    <dt className="text-xs text-foreground-500">Commission / member (+)</dt>
                    <dd className="tnum mt-0.5 font-semibold text-primary-700">+ <CurrencyDisplay amount={result.commissionPerMember ?? Math.floor(commission / (auction.memberCount || 1))} hideSymbol /></dd>
                  </div>
                </>
              ) : null}
              <div>
                <dt className="text-xs text-foreground-500">Net installment</dt>
                <dd className="tnum mt-0.5 font-semibold text-foreground-950"><CurrencyDisplay amount={result.netInstallment} hideSymbol /></dd>
              </div>
            </dl>

            <div className={`mt-4 grid gap-2 ${commission > 0 ? "grid-cols-3" : "grid-cols-2"}`}>
              <div className="rounded-md bg-secondary-100 px-3 py-2.5 text-center">
                <p className="text-xs font-medium text-secondary-800">Total Collection</p>
                <p className="tnum mt-0.5 text-base font-bold text-secondary-900"><CurrencyDisplay amount={result.totalCollected} hideSymbol /></p>
                <p className="mt-0.5 text-[11px] text-secondary-700">Collected from all members</p>
              </div>
              <div className="rounded-md bg-accent-100 px-3 py-2.5 text-center">
                <p className="text-xs font-medium text-accent-800">Winner Prize</p>
                <p className="tnum mt-0.5 text-base font-bold text-accent-900"><CurrencyDisplay amount={result.winningBid} hideSymbol /></p>
                <p className="mt-0.5 text-[11px] text-accent-700">Paid to winning member</p>
              </div>
              {commission > 0 ? (
                <div className="rounded-md bg-primary-100 px-3 py-2.5 text-center">
                  <p className="text-xs font-medium text-primary-800">Organizer Commission</p>
                  <p className="tnum mt-0.5 text-base font-bold text-primary-900"><CurrencyDisplay amount={commission} hideSymbol /></p>
                  <p className="mt-0.5 text-[11px] text-primary-700">Credited to organizer account</p>
                </div>
              ) : null}
            </div>
            <p className="mt-2 text-xs text-success-700">
              {commission > 0
                ? `Total collection (${result.totalCollected.toLocaleString("en-IN")}) reconciles with Winner Prize (${result.winningBid.toLocaleString("en-IN")}) + Organizer Commission (${commission.toLocaleString("en-IN")}).`
                : result.dividendRemainder === 0
                ? "Collection exactly equals the payout — nothing is left over."
                : `Collection is payout plus a rounding remainder of ${result.dividendRemainder.toLocaleString("en-IN")}, reconciled separately.`}
            </p>
          </Card>

          <Card title="Bid history" subtitle="All bids, including any withdrawn">
            {history.length === 0 ? (
              <p className="text-sm text-foreground-500">No bids were recorded. This was the reserved organizer cycle.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="bg-background-100/60 text-left text-xs uppercase tracking-wide text-foreground-500">
                      <th className="px-4 py-2">Member</th>
                      <th className="px-4 py-2 text-right">Amount</th>
                      <th className="px-4 py-2 text-right">Discount</th>
                      <th className="px-4 py-2">Time</th>
                      <th className="px-4 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map((bid) => {
                      const isWinner = bid.memberId === result.winnerMemberId && bid.status === "standing";
                      const info = nameMap.get(bid.memberId);
                      return (
                        <tr key={bid.id} className={`border-t border-background-100 ${isWinner ? "bg-accent-50" : bid.status === "withdrawn" ? "opacity-50" : ""}`}>
                          <td className="px-4 py-2.5">
                            <span className="font-medium text-foreground-900">{info?.name ?? "Unknown"}</span>
                            <span className="tnum ml-2 text-xs text-foreground-400">{info?.ticket ?? ""}</span>
                            {isWinner ? <Badge className="ml-2" color="accent" dot>Winner</Badge> : null}
                          </td>
                          <td className="tnum px-4 py-2.5 text-right text-foreground-900"><CurrencyDisplay amount={bid.amount} hideSymbol /></td>
                          <td className="tnum px-4 py-2.5 text-right text-danger-600"><CurrencyDisplay amount={auction.pool - bid.amount} hideSymbol /></td>
                          <td className="tnum whitespace-nowrap px-4 py-2.5 text-xs text-foreground-500">{formatDateTime(bid.placedAt)}</td>
                          <td className="px-4 py-2.5">
                            {bid.status === "standing" ? <Badge variant="active">Standing</Badge> : <Badge variant="inactive">Withdrawn</Badge>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>

        <div>
          <Card title="Closed summary">
            <dl className="space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <dt className="text-foreground-600">Winner</dt>
                <dd className="font-medium text-foreground-900">{winner?.name ?? "—"}</dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-foreground-600">Winning bid</dt>
                <dd className="tnum font-medium text-accent-900"><CurrencyDisplay amount={result.winningBid} hideSymbol /></dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-foreground-600">Total discount</dt>
                <dd className="tnum font-medium text-danger-600"><CurrencyDisplay amount={result.discount} hideSymbol /></dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-foreground-600">Closed by</dt>
                <dd className="font-medium text-foreground-900">{result.closedBy}</dd>
              </div>
              <div className="flex items-center justify-between">
                <dt className="text-foreground-600">Closed at</dt>
                <dd className="tnum text-foreground-900">{formatDateTime(result.closedAt)}</dd>
              </div>
            </dl>
          </Card>
        </div>
      </div>
    </div>
  );
}